/**
 * Mobile Device Detection and Optimization Utilities
 * 
 * Provides utilities to detect mobile devices and adjust processing parameters
 * to handle large files more efficiently on mobile platforms.
 */

/**
 * Detect if the current device is a mobile device
 */
export function isMobileDevice(): boolean {
    // Check user agent for mobile indicators
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileKeywords = [
        'android',
        'webos',
        'iphone',
        'ipad',
        'ipod',
        'blackberry',
        'windows phone',
        'mobile'
    ];

    const isMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword));

    // Also check for touch support and small screen
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const hasSmallScreen = window.innerWidth <= 768;

    return isMobileUA || (hasTouchScreen && hasSmallScreen);
}

/**
 * Get the appropriate file size threshold for large file detection.
 *
 * Recordings above this size skip the inline base64-JSON path (`transcribe`)
 * and go through the binary `upload-to-assemblyai` path instead.
 *
 * The inline path inflates the audio by ~33% (base64) and must be parsed +
 * decoded inside a single edge function request (256MB memory / 2s CPU).
 * Bodies that exceed what the gateway/worker accepts are rejected before the
 * function runs, and the browser surfaces that as an opaque
 * "Failed to send a request to the Edge Function" (Sep 2026: a desktop
 * recording failed 8/8 attempts with zero worker log lines). 6MB payloads are
 * verified to work; 10MB is the ceiling for both device classes.
 */
export function getMobileFileThreshold(): number {
    const MOBILE_THRESHOLD = 10 * 1024 * 1024; // 10MB for mobile
    const DESKTOP_THRESHOLD = 10 * 1024 * 1024; // 10MB for desktop (was 25MB - too large for inline base64)

    return isMobileDevice() ? MOBILE_THRESHOLD : DESKTOP_THRESHOLD;
}

/**
 * Estimate available memory for processing
 * Returns a conservative estimate in bytes
 */
export function estimateAvailableMemory(): number {
    // @ts-ignore - performance.memory is Chrome-specific
    if (performance.memory) {
        // @ts-ignore
        const { jsHeapSizeLimit, usedJSHeapSize } = performance.memory;
        const available = jsHeapSizeLimit - usedJSHeapSize;

        // Return 50% of available memory as a conservative estimate
        return Math.floor(available * 0.5);
    }

    // Fallback estimates based on device type
    if (isMobileDevice()) {
        return 50 * 1024 * 1024; // 50MB conservative estimate for mobile
    }

    return 200 * 1024 * 1024; // 200MB conservative estimate for desktop
}

/**
 * Check if a file size is safe to process in memory
 */
export function isSafeToProcessInMemory(fileSize: number): boolean {
    const availableMemory = estimateAvailableMemory();

    // Base64 encoding increases size by ~33%, plus we need overhead for processing
    const estimatedMemoryNeeded = fileSize * 1.5;

    return estimatedMemoryNeeded < availableMemory;
}

/**
 * Get device information for logging and diagnostics
 */
export function getDeviceInfo() {
    return {
        isMobile: isMobileDevice(),
        userAgent: navigator.userAgent,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
        touchSupport: 'ontouchstart' in window,
        maxTouchPoints: navigator.maxTouchPoints,
        platform: navigator.platform,
        fileThreshold: getMobileFileThreshold(),
        estimatedMemory: estimateAvailableMemory(),
        // @ts-ignore
        actualMemory: performance.memory ? {
            // @ts-ignore
            limit: performance.memory.jsHeapSizeLimit,
            // @ts-ignore
            used: performance.memory.usedJSHeapSize,
            // @ts-ignore
            total: performance.memory.totalJSHeapSize
        } : null
    };
}

/**
 * Log device information for debugging
 */
export function logDeviceInfo() {
    const info = getDeviceInfo();
    console.log('📱 Device Information:', {
        type: info.isMobile ? 'Mobile' : 'Desktop',
        screen: `${info.screenWidth}x${info.screenHeight}`,
        fileThreshold: `${(info.fileThreshold / 1024 / 1024).toFixed(1)}MB`,
        estimatedMemory: `${(info.estimatedMemory / 1024 / 1024).toFixed(1)}MB`,
        platform: info.platform
    });

    return info;
}
